import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    FormGroup,
    FormControlLabel,
    Switch,
    Box,
    Paper,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Button,
    TextareaAutosize,
    Stack,
    Grid
} from '@mui/material';

// --- Definición de IDs para las 4 Salidas AC ---
const acOutputIds = Array.from({ length: 4 }, (_, i) => i); // [0, 1, 2, 3]

// --- Definición de la estructura inicial por defecto para una Salida AC ---
const defaultAcOutputConfig = {
    hab: true,
    FijoOn: { h: 0, m: 5, s: 0 }, // 300 segundos = 00:05:00
    AleatorioOn: { h: 0, m: 5, s: 0 }, // 300 segundos = 00:05:00
    FijoOff: { h: 0, m: 5, s: 0 }, // 300 segundos = 00:05:00
    AleatorioOff: { h: 0, m: 5, s: 0 }, // 300 segundos = 00:05:00
    HoraDesde: { h: 8, m: 0 }, // 480 minutos = 08:00
    HoraHasta: { h: 18, m: 0 }, // 1080 minutos = 18:00
    DescCorta: "---"
};

// Función auxiliar para inicializar el estado de todas las Salidas AC
const initializeAcOutputsData = (initialJsonData = null) => {
    const initialState = {};
    acOutputIds.forEach((outputId) => {
        const initialOutputData = initialJsonData?.find(item => item.id === outputId);
        if (initialOutputData) {
            // Convertir de segundos/minutos totales a componentes HH/MM/SS
            const hmsToComponents = (totalSeconds) => {
                const h = Math.floor(totalSeconds / 3600);
                const r1 = totalSeconds % 3600;
                const m = Math.floor(r1 / 60);
                const s = r1 % 60;
                return { h, m, s };
            };
            const hmToComponents = (totalMinutes) => {
                const h = Math.floor(totalMinutes / 60);
                const m = totalMinutes % 60;
                return { h, m };
            };

            initialState[outputId] = {
                id: outputId,
                hab: initialOutputData.hab,
                FijoOn: hmsToComponents(initialOutputData.FijoOn),
                AleatorioOn: hmsToComponents(initialOutputData.AleatorioOn),
                FijoOff: hmsToComponents(initialOutputData.FijoOff),
                AleatorioOff: hmsToComponents(initialOutputData.AleatorioOff),
                HoraDesde: hmToComponents(initialOutputData.HoraDesde),
                HoraHasta: hmToComponents(initialOutputData.HoraHasta),
                DescCorta: initialOutputData.DescCorta,
            };
        } else {
            initialState[outputId] = {
                id: outputId,
                ...defaultAcOutputConfig
            };
        }
    });
    return initialState;
};


function AcOutputConfigForm({ initialData, setInitialData }) {
    // Estado para todos los datos de las 4 Salidas AC
    const [allAcOutputsData, setAllAcOutputsData] = useState(() => initializeAcOutputsData(initialData));
    // Estado para la Salida AC actualmente seleccionada en el dropdown
    const [selectedOutputId, setSelectedOutputId] = useState(() => {
        const initialId = initialData?.[0]?.id ?? 0;
        return acOutputIds.includes(initialId) ? initialId : acOutputIds[0];
    });

    // Estado para el JSON que se muestra en el Textarea
    const [jsonOutput, setJsonOutput] = useState('');

    // Referencia para almacenar la configuración ORIGINAL (para el botón CANCELAR y dirty check)
    const originalConfigRef = useRef(null);

    // Estado para controlar si el formulario tiene cambios no guardados
    const [formDirty, setFormDirty] = useState(false);
    // Estado para controlar si el JSON mostrado está actualizado con los datos del formulario
    const [jsonNeedsUpdate, setJsonNeedsUpdate] = useState(true); // Inicialmente true para forzar un "Generar JSON"

    // --- useEffect para inicializar originalConfigRef y setInitialData al montar ---
    useEffect(() => {
        const initialFormStateForRef = initializeAcOutputsData(initialData); // Usar esta para la referencia
        const initialFullConfig = generateFullConfigArray(initialFormStateForRef); // Generar JSON a partir de ella

        originalConfigRef.current = initialFullConfig;
        setJsonOutput(JSON.stringify(initialFullConfig, null, 2)); // Mostrar el JSON inicial
        setJsonNeedsUpdate(false); // Al inicio, el JSON mostrado está actualizado
        setFormDirty(false); // Al inicio, el formulario no está dirty

        // Sincronizar con el padre si la prop setInitialData está presente
        if (setInitialData) {
            setInitialData(initialFullConfig);
        }
    }, []); // Se ejecuta solo una vez al montar

    // --- useEffect para determinar si el formulario está "dirty" y si el JSON necesita actualización ---
    useEffect(() => {
        const currentFullConfig = generateFullConfigArray(allAcOutputsData);
        const currentFullConfigString = JSON.stringify(currentFullConfig, null, 2);
        const originalConfigString = JSON.stringify(originalConfigRef.current, null, 2);

        // Actualizar el estado 'formDirty'
        setFormDirty(currentFullConfigString !== originalConfigString);

        // Actualizar el estado 'jsonNeedsUpdate'
        setJsonNeedsUpdate(jsonOutput === '' || (currentFullConfigString !== jsonOutput));

        // Sincronizar con el padre
        if (setInitialData) {
            setInitialData(currentFullConfig);
        }
    }, [allAcOutputsData, jsonOutput, setInitialData]);


    // --- Función auxiliar para generar el array de configuración completo (el que se enviaría) ---
    const generateFullConfigArray = (currentAllOutputsData) => {
        // Ordenar las salidas por ID para asegurar un array consistente
        const sortedOutputs = Object.values(currentAllOutputsData).sort((a, b) => a.id - b.id);

        return sortedOutputs.map((outputData) => {
            // Asegurarse de que 'hab' sea 0 o 1 en el JSON
            const habValue = outputData.hab ? 1 : 0;

            // Convertir componentes de tiempo a segundos/minutos totales para el JSON
            const hmsToTotalSeconds = (timeObj) => {
                const h = Number(timeObj.h) || 0;
                const m = Number(timeObj.m) || 0;
                const s = Number(timeObj.s) || 0;
                // Aplicar límites de la tabla
                let total = h * 3600 + m * 60 + s;
                const minSec = 10;
                const maxSec = 86400;
                if (total < minSec) total = minSec;
                if (total > maxSec) total = maxSec;
                return total;
            };

            const hmToTotalMinutes = (timeObj) => {
                const h = Number(timeObj.h) || 0;
                const m = Number(timeObj.m) || 0;
                // Aplicar límites de la tabla
                let total = h * 60 + m;
                const minMin = 0;
                const maxMin = 1440; // 24 * 60
                if (total < minMin) total = minMin;
                if (total > maxMin) total = maxMin;
                return total;
            };

            return {
                id: outputData.id,
                hab: habValue,
                FijoOn: hmsToTotalSeconds(outputData.FijoOn),
                AleatorioOn: hmsToTotalSeconds(outputData.AleatorioOn),
                FijoOff: hmsToTotalSeconds(outputData.FijoOff),
                AleatorioOff: hmsToTotalSeconds(outputData.AleatorioOff),
                HoraDesde: hmToTotalMinutes(outputData.HoraDesde),
                HoraHasta: hmToTotalMinutes(outputData.HoraHasta),
                DescCorta: outputData.DescCorta,
            };
        });
    };

    // --- Manejadores de cambios ---
    const handleOutputChange = (event) => {
        setSelectedOutputId(Number(event.target.value)); // Convertir a número
    };

    // Maneja cambios para campos booleanos y de texto
    const handleFieldChange = (field, type) => (event) => {
        let value = event.target.value;

        setAllAcOutputsData(prevAllConfigs => {
            const currentOutputConfig = { ...prevAllConfigs[selectedOutputId] };

            if (type === 'boolean') {
                value = event.target.checked;
                currentOutputConfig[field] = value;
            } else if (type === 'text') {
                const maxLength = 15;
                if (value.length > maxLength) {
                    value = value.substring(0, maxLength);
                }
                currentOutputConfig[field] = value;
            }

            return {
                ...prevAllConfigs,
                [selectedOutputId]: currentOutputConfig,
            };
        });
    };

    // Maneja cambios para campos de tiempo (HH, MM, SS)
    const handleTimeComponentChange = (timeField, component) => (event) => {
        let value = event.target.value; // Permite que el string esté vacío durante la edición

        setAllAcOutputsData(prevAllConfigs => {
            const currentOutputConfig = { ...prevAllConfigs[selectedOutputId] };
            currentOutputConfig[timeField] = {
                ...currentOutputConfig[timeField],
                [component]: value // Almacenar como string, se valida en blur
            };
            return {
                ...prevAllConfigs,
                [selectedOutputId]: currentOutputConfig,
            };
        });
    };

    // Aplica validaciones y límites al salir de un campo de tiempo
    const handleTimeComponentBlur = (timeField, component, type) => (event) => {
        let value = event.target.value;
        let parsedValue = parseInt(value, 10);

        if (value === '' || isNaN(parsedValue)) {
            parsedValue = 0; // Si está vacío o no es un número, se convierte a 0 para el estado
        }

        let maxVal;
        if (type === 'hms') { // Hours, Minutes, Seconds for HH:MM:SS
            maxVal = (component === 'h' ? 23 : 59);
        } else { // Hours, Minutes for HH:MM
            maxVal = (component === 'h' ? 23 : 59);
        }

        parsedValue = Math.max(0, Math.min(parsedValue, maxVal)); // Aplicar min 0 y max específico

        setAllAcOutputsData(prevAllConfigs => {
            const currentOutputConfig = { ...prevAllConfigs[selectedOutputId] };
            currentOutputConfig[timeField] = {
                ...currentOutputConfig[timeField],
                [component]: parsedValue // Guardar el valor numérico validado
            };
            return {
                ...prevAllConfigs,
                [selectedOutputId]: currentOutputConfig,
            };
        });
    };


    // --- Funciones de los botones ---
    const handleGenerateJson = () => {
        const currentFullConfig = generateFullConfigArray(allAcOutputsData);
        setJsonOutput(JSON.stringify(currentFullConfig, null, 2));
        setJsonNeedsUpdate(false); // El JSON mostrado está ahora actualizado
    };

    const sendConfig = () => {
        const configToSend = generateFullConfigArray(allAcOutputsData);

        //console.log("--- Enviando Configuración de Salidas AC JSON ---");
        //console.log(configToSend);
        alert("Configuración de Salidas AC enviada (simulada). Revisa la consola.");

        // Si el envío es exitoso, actualizamos la configuración original
        originalConfigRef.current = configToSend;
        setFormDirty(false); // Ya no hay cambios pendientes
        setJsonNeedsUpdate(false); // El JSON mostrado (si se generó antes de enviar) es el que se envió
        setJsonOutput(JSON.stringify(configToSend, null, 2)); // Asegurarse de que el JSON mostrado sea el enviado
    };

    const handleCancel = () => {
        if (formDirty) {
            const confirmCancel = window.confirm(
                "¿Estás seguro de que quieres cancelar? Todos los cambios no guardados se perderán."
            );
            if (confirmCancel) {
                // Restaurar la configuración a los valores originales
                const restoredAcOutputsData = initializeAcOutputsData(originalConfigRef.current);

                setAllAcOutputsData(restoredAcOutputsData);
                // Asegurarse de que selectedOutputId sea válido después de la restauración
                const initialIdAfterRestore = originalConfigRef.current?.[0]?.id ?? 0;
                setSelectedOutputId(acOutputIds.includes(initialIdAfterRestore) ? initialIdAfterRestore : acOutputIds[0]);

                setJsonOutput(''); // Limpiar JSON previsualizado
                setJsonNeedsUpdate(true);
                setFormDirty(false);

                alert("Cambios descartados. Formulario restaurado a los valores originales.");
            }
        } else {
            alert("No hay cambios que descartar.");
        }
    };

    // Datos de la Salida AC actualmente seleccionada para renderizar
    const currentAcOutputData = allAcOutputsData[selectedOutputId];

    // Helper para renderizar los campos de tiempo (HH:MM:SS)
    const renderHmsFields = (fieldKey, labelBase) => (
        <Box key={fieldKey} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: '4px', p: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {labelBase}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                    label="HH"
                    type="number"
                    value={currentAcOutputData[fieldKey].h === '' ? '' : String(currentAcOutputData[fieldKey].h).padStart(2, '0')}
                    onChange={handleTimeComponentChange(fieldKey, 'h')}
                    onBlur={handleTimeComponentBlur(fieldKey, 'h', 'hms')}
                    inputProps={{ min: 0, max: 23, style: { textAlign: 'center' } }}
                    size="small"
                    sx={{ width: 70 }}
                />
                <Typography variant="h6">:</Typography>
                <TextField
                    label="MM"
                    type="number"
                    value={currentAcOutputData[fieldKey].m === '' ? '' : String(currentAcOutputData[fieldKey].m).padStart(2, '0')}
                    onChange={handleTimeComponentChange(fieldKey, 'm')}
                    onBlur={handleTimeComponentBlur(fieldKey, 'm', 'hms')}
                    inputProps={{ min: 0, max: 59, style: { textAlign: 'center' } }}
                    size="small"
                    sx={{ width: 70 }}
                />
                <Typography variant="h6">:</Typography>
                <TextField
                    label="SS"
                    type="number"
                    value={currentAcOutputData[fieldKey].s === '' ? '' : String(currentAcOutputData[fieldKey].s).padStart(2, '0')}
                    onChange={handleTimeComponentChange(fieldKey, 's')}
                    onBlur={handleTimeComponentBlur(fieldKey, 's', 'hms')}
                    inputProps={{ min: 0, max: 59, style: { textAlign: 'center' } }}
                    size="small"
                    sx={{ width: 70 }}
                />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Rango: 00:00:10 a 23:59:59
            </Typography>
        </Box>
    );

    // Helper para renderizar los campos de tiempo (HH:MM)
    const renderHmFields = (fieldKey, labelBase) => (
        <Box key={fieldKey} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: '4px', p: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {labelBase}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                    label="HH"
                    type="number"
                    value={currentAcOutputData[fieldKey].h === '' ? '' : String(currentAcOutputData[fieldKey].h).padStart(2, '0')}
                    onChange={handleTimeComponentChange(fieldKey, 'h')}
                    onBlur={handleTimeComponentBlur(fieldKey, 'h', 'hm')}
                    inputProps={{ min: 0, max: 23, style: { textAlign: 'center' } }}
                    size="small"
                    sx={{ width: 70 }}
                />
                <Typography variant="h6">:</Typography>
                <TextField
                    label="MM"
                    type="number"
                    value={currentAcOutputData[fieldKey].m === '' ? '' : String(currentAcOutputData[fieldKey].m).padStart(2, '0')}
                    onChange={handleTimeComponentChange(fieldKey, 'm')}
                    onBlur={handleTimeComponentBlur(fieldKey, 'm', 'hm')}
                    inputProps={{ min: 0, max: 59, style: { textAlign: 'center' } }}
                    size="small"
                    sx={{ width: 70 }}
                />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Rango: 00:00 (0 min) a 23:59 (1439 min)
            </Typography>
        </Box>
    );

    // Helper para convertir HH:MM a string
    const formatHm = (timeObj) => {
        if (!timeObj) return '';
        const h = String(timeObj.h).padStart(2, '0');
        const m = String(timeObj.m).padStart(2, '0');
        return `${h}:${m}`;
    };

    // Helper para convertir HH:MM:SS a string
    const formatHms = (timeObj) => {
        if (!timeObj) return '';
        const h = String(timeObj.h).padStart(2, '0');
        const m = String(timeObj.m).padStart(2, '0');
        const s = String(timeObj.s).padStart(2, '0');
        return `${h}:${m}:${s}`;
    };


    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h1" gutterBottom>
                    Configuración de Salidas AC
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                    {/* Columna izquierda: Selección de Salida AC y Campos */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', position: 'relative', pt: 4 }}>
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Salidas AC
                            </Typography>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel id="ac-output-select-label">Seleccionar Salida AC</InputLabel>
                                <Select
                                    labelId="ac-output-select-label"
                                    id="ac-output-select"
                                    value={selectedOutputId}
                                    label="Seleccionar Salida AC"
                                    onChange={handleOutputChange}
                                >
                                    {acOutputIds.map((id) => (
                                        <MenuItem key={id} value={id}>
                                            Salida AC {id}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Typography variant="subtitle1" component="p" gutterBottom sx={{mb: 2}}>
                                Datos para Salida AC {selectedOutputId}:
                            </Typography>

                            {currentAcOutputData && (
                                <>
                                    <FormGroup sx={{mb: 2}}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={currentAcOutputData.hab}
                                                    onChange={handleFieldChange('hab', 'boolean')}
                                                    name="hab"
                                                    color="primary"
                                                />
                                            }
                                            label="Habilitar"
                                        />
                                    </FormGroup>

                                    {renderHmsFields('FijoOn', 'Fijo Encendido')}
                                    {renderHmsFields('AleatorioOn', 'Aleatorio Encendido')}
                                    {renderHmsFields('FijoOff', 'Fijo Apagado')}
                                    {renderHmsFields('AleatorioOff', 'Aleatorio Apagado')}

                                    {renderHmFields('HoraDesde', 'Hora Desde')}
                                    {renderHmFields('HoraHasta', 'Hora Hasta')}

                                    <TextField
                                        label="Descripción Corta"
                                        type="text"
                                        value={currentAcOutputData.DescCorta || ''}
                                        onChange={handleFieldChange('DescCorta', 'text')}
                                        fullWidth
                                        margin="normal"
                                        size="small"
                                        inputProps={{ maxLength: 15 }}
                                        helperText="Máximo 15 caracteres"
                                    />
                                </>
                            )}
                        </Paper>
                    </Grid>

                    {/* Columna derecha: JSON generado y Resumen de Configuración */}
                    <Grid item xs={12} md={6}>
                        {/* JSON generado */}
                        <Paper variant="outlined" sx={{ p: 2, height: 'auto', display: 'flex', flexDirection: 'column', position: 'relative', pt: 4, mb: 3 }}>
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                }}
                            >
                                JSON de Configuración
                            </Typography>
                            {jsonOutput ? (
                                <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                                    <TextareaAutosize
                                        value={jsonOutput}
                                        minRows={10}
                                        maxRows={20}
                                        style={{
                                            width: '100%',
                                            border: `1px solid ${jsonNeedsUpdate ? '#ff9800' : '#ccc'}`,
                                            borderRadius: '4px',
                                            padding: '8px',
                                            fontFamily: 'monospace',
                                            resize: 'vertical',
                                            backgroundColor: jsonNeedsUpdate ? '#ffe0b2' : 'white',
                                        }}
                                        readOnly
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography color="text.secondary">
                                        Genera el JSON para previsualizarlo aquí.
                                    </Typography>
                                </Box>
                            )}
                            <Button
                                variant="contained"
                                color={jsonNeedsUpdate ? "warning" : "primary"}
                                onClick={handleGenerateJson}
                                sx={{ mt: 'auto' }}
                                startIcon={jsonNeedsUpdate ? <i className="fa-solid fa-triangle-exclamation"></i> : null}
                            >
                                {jsonNeedsUpdate ? "Actualizar JSON" : "Generar JSON"}
                            </Button>
                        </Paper>

                        {/* Resumen de la configuración y estado del formulario */}
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff', position: 'relative', pt: 4 }}>
                            <Typography
                                variant="caption"
                                component="div"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 12,
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'background.paper',
                                    px: 1,
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Resumen de Configuración
                            </Typography>
                            <Typography variant="h6" gutterBottom>Salida AC {selectedOutputId}</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                        **Habilitar:** {currentAcOutputData?.hab ? 'Sí' : 'No'}
                                    </Typography>
                                    <Typography variant="body2">
                                        **Fijo Encendido:** {formatHms(currentAcOutputData?.FijoOn)}
                                    </Typography>
                                    <Typography variant="body2">
                                        **Aleatorio Encendido:** {formatHms(currentAcOutputData?.AleatorioOn)}
                                    </Typography>
                                    <Typography variant="body2">
                                        **Fijo Apagado:** {formatHms(currentAcOutputData?.FijoOff)}
                                    </Typography>
                                    <Typography variant="body2">
                                        **Aleatorio Apagado:** {formatHms(currentAcOutputData?.AleatorioOff)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                        **Hora Desde:** {formatHm(currentAcOutputData?.HoraDesde)}
                                    </Typography>
                                    <Typography variant="body2">
                                        **Hora Hasta:** {formatHm(currentAcOutputData?.HoraHasta)}
                                    </Typography>
                                    <Typography variant="body2">
                                        **Descripción Corta:** {currentAcOutputData?.DescCorta || 'N/A'}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" color={formDirty ? 'error' : 'success.main'}>
                                            **Formulario Modificado:** {formDirty ? 'Sí' : 'No'}
                                        </Typography>
                                        <Typography variant="body2" color={jsonNeedsUpdate ? 'warning.main' : 'success.main'}>
                                            **JSON Actualizado:** {jsonNeedsUpdate ? 'No' : 'Sí'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Fila inferior: Botones de acción general (CANCELAR, ENVIAR) */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleCancel}
                                    startIcon={<i className="fa-solid fa-xmark"></i>}
                                    disabled={!formDirty}
                                >
                                    CANCELAR
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={sendConfig}
                                    startIcon={<i className="fa-solid fa-paper-plane"></i>}
                                    disabled={jsonNeedsUpdate || !formDirty}
                                >
                                    ENVIAR
                                </Button>
                            </Stack>
                        </Box>
                    </Grid>

                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                    * Configuración de los parámetros para las Salidas AC.
                </Typography>
            </Paper>
        </Container>
    );
}

export default AcOutputConfigForm;